import { useState, useEffect } from "react";
import { H1Title, Paragraph } from "../components/typography/typography";
import { AppButton } from "../components/buttons/button";
import AuthGeneralInput from "../components/input/authinput";
import SubmissionService from "../services/submissionService";
import CompetitionService from "../services/competitionService";
import { Success, Error as ToastError } from "../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faCheckCircle, faClock, faTrash } from "@fortawesome/free-solid-svg-icons";

export default function Fundraising() {
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', amount: '', proofUrl: '' });
  const [adding, setAdding] = useState(false);

  useEffect(() => { loadSubmission(); }, []);

  const loadSubmission = async () => {
    try {
      const { data: compData } = await CompetitionService.getActive();
      const { data } = await SubmissionService.getMySubmission(compData.data.competition._id);
      setSubmission(data.data.submission);
    } catch { /* no submission */ }
    finally { setLoading(false); }
  };

  const handleAddSource = async (e) => {
    e.preventDefault();
    if (!form.name || !form.amount) { ToastError('Name and amount required'); return; }
    setAdding(true);
    try {
      await SubmissionService.addFundraisingSource(submission._id, {
        name: form.name,
        amount: parseFloat(form.amount),
        proofUrl: form.proofUrl
      });
      await loadSubmission();
      setForm({ name: '', amount: '', proofUrl: '' });
      setShowForm(false);
      Success('Source added');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to add');
    } finally { setAdding(false); }
  };

  const handleDeleteSource = async (sourceId) => {
    if (!confirm('Remove this source?')) return;
    try {
      await SubmissionService.deleteFundraisingSource(submission._id, sourceId);
      await loadSubmission();
      Success('Source removed');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to remove');
    }
  };

  if (loading) return <div className="py-20 text-center text-grey-667">Loading...</div>;

  if (!submission) {
    return (
      <div className="max-w-lg mx-auto text-center py-20">
        <H1Title size="sm">Fundraising</H1Title>
        <Paragraph className="text-grey-667 mt-2">Submit your proposal first to access fundraising.</Paragraph>
      </div>
    );
  }

  const target = submission.fundraising?.targetAmount || 5000;
  const sources = submission.fundraising?.sources || [];
  const verifiedTotal = sources.filter(s => s.verifiedAt).reduce((sum, s) => sum + s.amount, 0);
  const reportedTotal = sources.reduce((sum, s) => sum + s.amount, 0);
  const progress = Math.min((verifiedTotal / target) * 100, 100);

  return (
    <div className="space-y-6 max-w-2xl">
      <H1Title size="sm">Fundraising Progress</H1Title>

      {/* Progress Card */}
      <div className="bg-white rounded-xl border border-grey-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-grey-667">Verified: <span className="font-bold text-green">${verifiedTotal.toLocaleString()}</span></p>
          <p className="text-sm text-grey-667">Target: <span className="font-bold">${target.toLocaleString()}</span></p>
        </div>
        <div className="w-full bg-grey-200 rounded-full h-4 mb-2">
          <div className="bg-green h-4 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex justify-between text-xs text-grey-98">
          <span>{Math.round(progress)}% verified</span>
          <span>Reported: ${reportedTotal.toLocaleString()}</span>
        </div>
      </div>

      {/* Sources */}
      <div className="bg-white rounded-xl border border-grey-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">Fundraising Sources</h3>
          <AppButton onClick={() => setShowForm(!showForm)} variant="outline" size="sm" fullWidth={false} className="px-4">
            <FontAwesomeIcon icon={faPlus} className="mr-1" /> Add Source
          </AppButton>
        </div>

        {showForm && (
          <form onSubmit={handleAddSource} className="border border-grey-200 rounded-lg p-4 mb-4 space-y-3">
            <AuthGeneralInput label="Source Name" name="name" placeholder="e.g. LaunchGood Campaign" value={form.name} change={(e) => setForm({ ...form, name: e.target.value })} />
            <AuthGeneralInput label="Amount ($)" type="number" name="amount" placeholder="0" value={form.amount} change={(e) => setForm({ ...form, amount: e.target.value })} />
            <AuthGeneralInput label="Proof URL (optional)" type="url" name="proofUrl" placeholder="https://..." value={form.proofUrl} change={(e) => setForm({ ...form, proofUrl: e.target.value })} notrequired />
            <div className="flex gap-2">
              <AppButton type="button" variant="outline" onClick={() => setShowForm(false)} fullWidth={false} size="sm" className="px-4">Cancel</AppButton>
              <AppButton type="submit" size="sm" fullWidth={false} className="px-4" loading={adding}>Add Source</AppButton>
            </div>
          </form>
        )}

        {sources.length === 0 ? (
          <p className="text-sm text-grey-667 text-center py-6">No fundraising sources added yet</p>
        ) : (
          <div className="divide-y divide-grey-200">
            {sources.map((s) => (
              <div key={s._id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-semibold">{s.name}</p>
                  {s.proofUrl && (
                    <a href={s.proofUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">View proof</a>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <p className="font-bold text-sm">${s.amount.toLocaleString()}</p>
                  {s.verifiedAt ? (
                    <span className="text-xs bg-green-light text-green px-2 py-1 rounded-full">
                      <FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Verified
                    </span>
                  ) : (
                    <span className="text-xs bg-yellow-500/10 text-orange-dark px-2 py-1 rounded-full">
                      <FontAwesomeIcon icon={faClock} className="mr-1" /> Pending
                    </span>
                  )}
                  {!s.verifiedAt && (
                    <button onClick={() => handleDeleteSource(s._id)} className="text-grey-98 hover:text-red-500 cursor-pointer" title="Remove source">
                      <FontAwesomeIcon icon={faTrash} className="text-xs" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
